"use client";

type UploadProgress = { percentage: number };

export type StorageClientUploadOptions = {
  handleUploadUrl: string;
  clientPayload?: string;
  onUploadProgress?: (progress: UploadProgress) => void;
  signal?: AbortSignal;
};

export type StorageUploadTicket =
  | {
      mode: "signed";
      uploadUrl: string;
      contentType?: string;
      expiresAt: string;
    }
  | {
      mode: "multipart";
      uploadId: string;
      partSize: number;
      parts: Array<{ partNumber: number; url: string }>;
      expiresAt: string;
    };

export type StorageTicketUploadOptions = {
  onUploadProgress?: (progress: UploadProgress) => void;
  signal?: AbortSignal;
  completeMultipart?: (parts: Array<{ partNumber: number; etag: string }>) => Promise<void>;
  abortMultipart?: () => Promise<void>;
};

type SignedUploadResponse =
  | {
      provider: "supabase" | "backblaze-b2";
      mode: "signed";
      uploadUrl: string;
      pathname: string;
      upsert: boolean;
      expiresAt: string;
    }
  | {
      provider: "supabase" | "backblaze-b2";
      mode: "multipart";
      uploadId: string;
      partSize: number;
      parts: Array<{ partNumber: number; url: string }>;
      pathname: string;
      upsert: boolean;
      expiresAt: string;
    };

export type StorageClientUploadResult = {
  pathname: string;
};

async function ticketStorage(pathname: string, file: Blob, options: StorageClientUploadOptions) {
  const response = await fetch(options.handleUploadUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      pathname,
      contentType: file.type,
      tamanho: file.size,
      clientPayload: options.clientPayload,
    }),
  });

  const data = await response.json().catch(() => ({})) as SignedUploadResponse & { erro?: string };
  if (!response.ok) throw new Error(data.erro ?? "Nao foi possivel autorizar o upload.");
  return data;
}

function putDirect(input: {
  url: string;
  body: Blob;
  headers?: Record<string, string>;
  completedBytes: number;
  totalBytes: number;
  onUploadProgress?: (progress: UploadProgress) => void;
  signal?: AbortSignal;
  requireEtag?: boolean;
}) {
  return new Promise<string | undefined>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let settled = false;
    const fail = (error: Error) => {
      if (settled) return;
      settled = true;
      reject(error);
    };
    const onAbort = () => {
      xhr.abort();
      fail(new Error("Upload cancelado."));
    };
    if (input.signal?.aborted) return fail(new Error("Upload cancelado."));

    input.signal?.addEventListener("abort", onAbort, { once: true });
    xhr.open("PUT", input.url);
    Object.entries(input.headers ?? {}).forEach(([name, value]) => xhr.setRequestHeader(name, value));
    xhr.upload.onprogress = (event) => {
      const loaded = event.lengthComputable ? event.loaded : 0;
      input.onUploadProgress?.({
        percentage: input.totalBytes ? ((input.completedBytes + loaded) / input.totalBytes) * 100 : 0,
      });
    };
    xhr.onerror = () => fail(new Error("Falha de rede no upload."));
    xhr.onabort = () => fail(new Error("Upload cancelado."));
    xhr.onload = () => {
      if (settled) return;
      settled = true;
      input.signal?.removeEventListener("abort", onAbort);
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(`Storage HTTP ${xhr.status}.`));
        return;
      }
      const etag = xhr.getResponseHeader("ETag")?.trim();
      if (input.requireEtag && !etag) {
        reject(new Error("Storage nao retornou ETag da parte."));
        return;
      }
      resolve(etag);
    };
    xhr.send(input.body);
  });
}

async function uploadSignedStandard(
  file: Blob,
  ticket: Extract<StorageUploadTicket, { mode: "signed" }>,
  options: Pick<StorageClientUploadOptions, "onUploadProgress" | "signal">,
) {
  await putDirect({
    url: ticket.uploadUrl,
    body: file,
    headers: {
      "Content-Type": ticket.contentType || file.type || "application/octet-stream",
    },
    completedBytes: 0,
    totalBytes: file.size,
    onUploadProgress: options.onUploadProgress,
    signal: options.signal,
  });
}

async function postStorageOperation(
  pathname: string,
  file: Blob,
  options: StorageClientUploadOptions,
  operation: "complete" | "abort",
  uploadId: string,
  parts?: Array<{ partNumber: number; etag: string }>,
) {
  const response = await fetch(options.handleUploadUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      storageOperation: operation,
      pathname,
      uploadId,
      parts,
      tamanho: file.size,
      contentType: file.type,
      clientPayload: options.clientPayload,
    }),
  });
  const data = await response.json().catch(() => ({})) as { erro?: string; pathname?: string };
  if (!response.ok) {
    throw new Error(data.erro ?? `Nao foi possivel ${operation === "complete" ? "concluir" : "cancelar"} o upload.`);
  }
  return data;
}

async function uploadMultipart(
  file: Blob,
  ticket: Extract<StorageUploadTicket, { mode: "multipart" }>,
  options: Pick<StorageClientUploadOptions, "onUploadProgress" | "signal">,
  completeMultipart: (parts: Array<{ partNumber: number; etag: string }>) => Promise<void>,
  abortMultipart?: () => Promise<void>,
) {
  let completedBytes = 0;
  const completedParts: Array<{ partNumber: number; etag: string }> = [];
  try {
    for (const part of ticket.parts) {
      const start = (part.partNumber - 1) * ticket.partSize;
      const end = Math.min(start + ticket.partSize, file.size);
      const chunk = file.slice(start, end);
      const etag = await putDirect({
        url: part.url,
        body: chunk,
        completedBytes,
        totalBytes: file.size,
        onUploadProgress: options.onUploadProgress,
        signal: options.signal,
        requireEtag: true,
      });
      completedParts.push({ partNumber: part.partNumber, etag: etag as string });
      completedBytes += chunk.size;
      options.onUploadProgress?.({ percentage: file.size ? (completedBytes / file.size) * 100 : 100 });
    }

    await completeMultipart(completedParts);
  } catch (error) {
    await abortMultipart?.().catch(() => undefined);
    throw error;
  }
}

export async function uploadWithStorageTicket(
  file: Blob,
  ticket: StorageUploadTicket,
  options: StorageTicketUploadOptions,
): Promise<void> {
  if (ticket.mode === "signed") {
    await uploadSignedStandard(file, ticket, options);
    return;
  }

  if (!options.completeMultipart) {
    throw new Error("O upload multipart não possui confirmação configurada.");
  }

  await uploadMultipart(file, ticket, options, options.completeMultipart, options.abortMultipart);
}

export async function uploadWithStorage(
  pathname: string,
  file: Blob,
  options: StorageClientUploadOptions,
): Promise<StorageClientUploadResult> {
  const ticket = await ticketStorage(pathname, file, options);
  if (ticket.mode === "signed") {
    await uploadSignedStandard(file, ticket, options);
    return { pathname: ticket.pathname };
  }

  let completedPathname = ticket.pathname;
  await uploadMultipart(
    file,
    ticket,
    options,
    async (parts) => {
      const completed = await postStorageOperation(ticket.pathname, file, options, "complete", ticket.uploadId, parts);
      completedPathname = completed.pathname ?? ticket.pathname;
    },
    async () => {
      await postStorageOperation(ticket.pathname, file, options, "abort", ticket.uploadId).catch(() => undefined);
    },
  );
  return { pathname: completedPathname };
}
